/**
 * Contract utilities for interacting with smart contracts
 *
 * This module provides functions to:
 * - Read ERC-20 token allowances from on-chain contracts
 * - Get contract bytecode to verify contract existence
 */
import { Contract } from "ethers";
import { erc20AllowanceAbi } from "../../data/abi/erc20";
import { normalizeChainID } from "../../shared/caip2";
/**
 * Get ERC-20 token allowance using RPC
 *
 * @remarks
 * - Upstream: ethers Contract.allowance via `ChainService.getRpcProvider()`
 * - Uses the ERC-20 allowance ABI to query the token contract
 * - Returns the allowance as a bigint (raw token units)
 *
 * @param tokenAddress - The ERC-20 token contract address
 * @param ownerAddress - The address of the token owner
 * @param spenderAddress - The address of the spender
 * @param chainService - ChainService instance providing RPC access
 * @param chainId - Chain ID (numeric or CAIP-2 format)
 * @param logger - Logger instance for request tracking
 * @param retry - RetryService for retry/backoff handling
 * @returns Promise resolving to the allowance as a bigint
 * @throws {Error} When contract call fails or addresses are invalid
 */
export async function getAllowanceRPC(tokenAddress, ownerAddress, spenderAddress, chainService, chainId, logger, retry) {
    try {
        const caip2 = normalizeChainID(chainId, "eip155");
        const provider = await chainService.getRpcProvider(caip2);
        logger.debug("Getting ERC-20 allowance on chain", `${caip2}:`, {
            tokenAddress,
            ownerAddress,
            spenderAddress,
        });
        // Create contract instance with the ERC-20 allowance ABI
        const contract = new Contract(tokenAddress, erc20AllowanceAbi, provider);
        // Call the allowance function
        const allowance = await retry.rpc(() => contract.allowance(ownerAddress, spenderAddress));
        logger.debug("Allowance retrieved:", allowance.toString());
        return allowance;
    }
    catch (error) {
        logger.error("Failed to get allowance:", error);
        throw new Error(`Allowance retrieval failed: ${error instanceof Error ? error.message : String(error)}`);
    }
}
/**
 * Get contract bytecode using RPC
 *
 * @remarks
 * - Upstream: ethers Provider.getCode via `ChainService.getRpcProvider()`
 * - Returns the bytecode deployed at the address
 * - Returns "0x" if no contract exists at the address (EOA or empty)
 * - Useful for verifying a contract exists before interacting with it
 *
 * @param address - The address to get bytecode from
 * @param chainService - ChainService instance providing RPC access
 * @param chainId - Chain ID (numeric or CAIP-2 format)
 * @param logger - Logger instance for request tracking
 * @param retry - RetryService for retry/backoff handling
 * @returns Promise resolving to the bytecode as a hex string
 * @throws {Error} When RPC call fails
 */
export async function getBytecodeRPC(address, chainService, chainId, logger, retry) {
    try {
        const caip2 = normalizeChainID(chainId, "eip155");
        const provider = await chainService.getRpcProvider(caip2);
        logger.debug("Getting bytecode on chain", `${caip2}:`, { address });
        // Get the code at the address
        const bytecode = await retry.rpc(() => provider.getCode(address));
        logger.debug("Bytecode retrieved, length:", bytecode.length);
        return bytecode;
    }
    catch (error) {
        logger.error("Failed to get bytecode:", error);
        throw new Error(`Bytecode retrieval failed: ${error instanceof Error ? error.message : String(error)}`);
    }
}
//# sourceMappingURL=data:application/json;base64,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